import { Communication } from '@app/model/Communication';
import { useApiHealthStore } from '@app/store/apiHealthStore';
import { useCommunicationStore } from '@app/store/communicationStore';
import { ApiError } from '@app/utils/ApiError';
import { useCallback, useEffect } from 'react';

export interface UseCommunicationsResult {
  communications: Communication[];
  isLoaded: boolean;
  /** Why the load failed, or null. */
  error: ApiError | null;
  /** Re-reads the catalog, ignoring the cache. Awaitable, so callers can show progress. */
  retry: () => Promise<void>;
}

/**
 * Keeps the communication catalog loaded for as long as something is mounted that reads it.
 *
 * The counterpart of useParameters, and mounted in one place rather than high up in the shell:
 * unlike a runtime setting, nothing outside the administration screen consults a communication,
 * so there is no reason to hold the catalog for a session that never opens that page.
 *
 * @param forceRefresh for the admin screen, which must show what the service is using right now
 *     rather than what the cache last heard.
 */
export const useCommunications = (forceRefresh = false): UseCommunicationsResult => {
  const communications = useCommunicationStore(state => state.items);
  const isLoaded = useCommunicationStore(state => state.isLoaded);
  const error = useCommunicationStore(state => state.error);
  const load = useCommunicationStore(state => state.load);

  // Bumped when the backend comes back, or when the user hits retry in the connectivity banner
  const retryToken = useApiHealthStore(state => state.retryToken);

  const retry = useCallback(() => load(true), [load]);

  useEffect(() => {
    // load() never rejects — a failure lands on the store's error, which this hook returns.
    void load(forceRefresh);
  }, [load, forceRefresh]);

  // Pick the catalog up once the service is reachable again, rather than leaving the screen on
  // whatever it managed to read before the outage.
  useEffect(() => {
    if (retryToken === 0) return;
    void load(true);
  }, [retryToken, load]);

  return { communications, isLoaded, error, retry };
};
